import styled, { keyframes, css } from 'styled-components';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _dark = clientThemePalette[CLIENT_THEME].dark;
const _light = clientThemePalette[CLIENT_THEME].light;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const queuingPulse = keyframes`
  0% { border-color: rgba(234, 179, 8, 0.4); box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.4); }
  70% { border-color: rgba(234, 179, 8, 1); box-shadow: 0 0 0 10px rgba(234, 179, 8, 0); }
  100% { border-color: rgba(234, 179, 8, 0.4); box-shadow: 0 0 0 0 rgba(234, 179, 8, 0); }
`;

export const slideFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* ───────── Layout ───────── */
export const CRWrapper = styled.div<{ $enableDarkTheme?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? _dark.pageBackground : _light.pageBackground};
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? _dark.textPrimary : _light.textPrimary};
  overflow: hidden;
`;

/* ───────── Header ───────── */
export const CRHeader = styled.header<{ $enableDarkTheme?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? _dark.topBar : _light.topBar};
  border-bottom: 1px solid
    ${({ $enableDarkTheme }) =>
      $enableDarkTheme ? alpha(_dark.primary, 0.12) : _light.borderGrey};
  backdrop-filter: blur(24px);
  flex-shrink: 0;
  z-index: 20;
  gap: 16px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
`;

export const PageTitle = styled.div``;

export const PageTitleMain = styled.h1`
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${_dark.primary};
  margin: 0;
  line-height: 1.2;
`;

export const PageTitleSub = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 9px;
  letter-spacing: 0.2em;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#ffffff' : _light.textSecondary};
  text-transform: uppercase;
  margin-top: 2px;
`;

export const OnlineDot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 9px;
  color: #16a34a;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 600;
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #16a34a;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

export const HeaderPanelTabs = styled.div`
  display: flex;
  gap: 6px;
  flex: 1;
  justify-content: center;
`;

export const PanelTab = styled.button<{
  $active: boolean;
  $enableDarkTheme?: boolean;
}>`
  padding: 6px 18px;
  border-radius: 6px;
  border: 1px solid
    ${({ $active, $enableDarkTheme }) =>
      $active
        ? alpha(_dark.primary, 0.55)
        : $enableDarkTheme
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'};
  background: ${({ $active, $enableDarkTheme }) =>
    $active
      ? alpha(_dark.primary, 0.12)
      : $enableDarkTheme
      ? 'transparent'
      : 'rgba(255,255,255,0.85)'};
  color: ${({ $active, $enableDarkTheme }) =>
    $active
      ? _dark.primary
      : $enableDarkTheme
      ? 'rgba(255,255,255,0.38)'
      : _light.textSecondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  &:hover {
    border-color: ${alpha(_dark.primary, 0.35)};
    color: ${_dark.primary};
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
`;

export const ShiftBadge = styled.div<{ $enableDarkTheme?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 18px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme
      ? alpha(_dark.primary, 0.07)
      : alpha(_light.primary, 0.04)};
  border: 1px solid ${alpha(_dark.primary, 0.22)};
  border-radius: 8px;
`;

export const ShiftBadgeLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 10px;
  letter-spacing: 0.1em;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.5)' : _light.textSecondary};
  text-transform: uppercase;
  font-weight: 700;
`;

export const ShiftRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

export const ShiftBadgeValue = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: ${_dark.primary};
  text-transform: uppercase;
`;

export const ShiftBadgeSync = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 11px;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.45)' : _light.textSecondary};
  font-weight: 500;
  margin-top: 4px;
  text-transform: none;
`;

export const LiveClock = styled.div`
  text-align: right;
`;

export const ClockTime = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 26px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#fff' : _light.textPrimary};
`;

export const ClockDate = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 10px;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.38)' : _light.textSecondary};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-top: 1px;
`;

/* ───────── Carousel ───────── */
export const CarouselOuter = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${alpha(_dark.primary, 0.28)};
    border-radius: 4px;
  }
`;

export const CarouselTrack = styled.div<{ $active: number }>`
  display: flex;
  width: 200%;
  height: 100%;
  transform: translateX(-${({ $active }) => $active * 50}%);
  transition: transform 0.52s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;
`;

export const CarouselPanel = styled.div`
  box-sizing: border-box;
  width: 50%;
  min-width: 50%;
  flex-shrink: 0;
  height: 100%;
  overflow-y: auto;
  padding: 24px 24px 72px;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${alpha(_dark.primary, 0.28)};
    border-radius: 4px;
  }
`;

export const CarouselNav = styled.div`
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 20;
`;

export const NavDot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '22px' : '7px')};
  height: 7px;
  border-radius: 4px;
  border: none;
  background: ${({ $active }) =>
    $active ? _dark.primary : 'rgba(255,255,255,0.18)'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
`;

export const NavArrow = styled.button`
  background: ${alpha(_dark.primary, 0.08)};
  border: 1px solid ${alpha(_dark.primary, 0.2)};
  color: ${_dark.primary};
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  transition: background 0.2s;
  font-family: inherit;
  &:hover {
    background: ${alpha(_dark.primary, 0.2)};
  }
  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
`;

/* ───────── Panel shared ───────── */
export const PanelTitle = styled.h2<{ $enableDarkTheme?: boolean }>`
  font-size: 13px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#ffffff' : _light.textSecondary};
  margin: 0 0 18px;
  font-weight: 700;
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

export const PaginationIndicator = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: ${_dark.primary};
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? alpha(_dark.primary, 0.1) : alpha(_light.primary, 0.05)};
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${alpha(_dark.primary, 0.2)};
  letter-spacing: 0.05em;
  min-width: 80px;
  text-align: center;
`;

export const PaginationButton = styled.button<{ $enableDarkTheme?: boolean }>`
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? alpha(_dark.primary, 0.1) : alpha(_light.primary, 0.05)};
  border: 1px solid ${alpha(_dark.primary, 0.2)};
  color: ${_dark.primary};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 900;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background: ${alpha(_dark.primary, 0.2)};
    border-color: ${alpha(_dark.primary, 0.4)};
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

export type BadgeVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral'
  | 'shift';

const getBadgeColors = (variant: BadgeVariant, isDark: boolean) => {
  switch (variant) {
    case 'success':
      return {
        color: '#16a34a',
        bg: 'rgba(22, 163, 74, 0.1)',
        border: isDark ? 'rgba(22, 163, 74, 0.2)' : 'rgba(22, 163, 74, 0.3)',
      };
    case 'error':
      return {
        color: '#f87171',
        bg: 'rgba(248, 113, 113, 0.1)',
        border: isDark
          ? 'rgba(248, 113, 113, 0.2)'
          : 'rgba(248, 113, 113, 0.3)',
      };
    case 'warning':
      return {
        color: '#fbbf24',
        bg: 'rgba(251, 191, 36, 0.1)',
        border: 'rgba(251, 191, 36, 0.25)',
      };
    case 'info':
      return {
        color: '#a5b4fc',
        bg: 'rgba(165, 180, 252, 0.1)',
        border: 'rgba(165, 180, 252, 0.25)',
      };
    case 'shift':
      return {
        color: _dark.primary,
        bg: alpha(_dark.primary, 0.1),
        border: alpha(_dark.primary, 0.3),
      };
    default:
      return {
        color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
        bg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        border: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
      };
  }
};

export const Badge = styled.div<{
  $variant: BadgeVariant;
  $size?: 'small' | 'large';
  $enableDarkTheme?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-weight: 700;
  border-radius: 4px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  line-height: 1.2;

  font-size: ${({ $size }) => ($size === 'large' ? '11px' : '9px')};
  padding: ${({ $size }) => ($size === 'large' ? '3px 8px' : '2px 6px')};
  letter-spacing: 0.04em;
  white-space: nowrap;

  color: ${({ $variant, $enableDarkTheme }) =>
    getBadgeColors($variant, !!$enableDarkTheme).color};
  background: ${({ $variant, $enableDarkTheme }) =>
    getBadgeColors($variant, !!$enableDarkTheme).bg};
  border: 1px solid
    ${({ $variant, $enableDarkTheme }) =>
      getBadgeColors($variant, !!$enableDarkTheme).border};
`;

export const SectionTitle = styled.h3<{ $enableDarkTheme?: boolean }>`
  font-size: 13px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.22)' : _light.textSecondary};
  font-weight: 700;
  margin: 28px 0 14px;
`;

/* ───────── Asset Grid ───────── */
export const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  column-gap: 14px;
  row-gap: 35px;
  animation: ${slideFadeIn} 0.5s ease-out;
  grid-auto-rows: 420px;
`;

export const ControlRoomMainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 24px;
  margin-top: 18px;
  width: 100%;

  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
    gap: 32px;

    /* Hide the vertical divider on single column layout */
    & > div:nth-child(2) {
      display: none;
    }
  }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const AssetCard = styled.div<{
  $ignitionOn?: boolean;
  $available?: boolean;
  $enableDarkTheme?: boolean;
  $queuing?: boolean;
}>`
  background: ${({ $enableDarkTheme, $available }) =>
    $available === false
      ? $enableDarkTheme
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.02)'
      : $enableDarkTheme
      ? `linear-gradient(145deg, ${alpha(_dark.boxBackground, 0.95)}, ${alpha(
          _dark.pageBackground,
          0.98
        )})`
      : 'linear-gradient(145deg, #ffffff, #f8fafc)'};
  opacity: ${({ $available, $ignitionOn }) =>
    $available === false || !$ignitionOn ? 0.7 : 1};
  filter: ${({ $ignitionOn }) => (!$ignitionOn ? 'grayscale(30%)' : 'none')};
  backdrop-filter: blur(20px);
  box-shadow: ${({ $enableDarkTheme }) =>
    $enableDarkTheme
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 8px 24px rgba(15, 23, 42, 0.06)'};
  border: 1px solid
    ${({ $ignitionOn }) =>
      $ignitionOn ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  border-left: 4px solid
    ${({ $ignitionOn }) => ($ignitionOn ? '#22c55e' : '#ef4444')};
  border-radius: 16px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  height: 100%;

  /* Queuing animation override */
  ${({ $queuing }) =>
    $queuing &&
    css`
      border-color: #eab308 !important;
      animation: ${queuingPulse} 2s infinite;
    `}
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: ${({ $ignitionOn }) =>
      $ignitionOn ? 'translateY(-4px)' : 'none'};
    box-shadow: ${({ $enableDarkTheme, $ignitionOn }) =>
      !$ignitionOn
        ? 'none'
        : $enableDarkTheme
        ? '0 20px 48px rgba(0, 0, 0, 0.6)'
        : '0 20px 40px rgba(15, 23, 42, 0.12)'};
    border-color: ${({ $ignitionOn }) =>
      $ignitionOn ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ $ignitionOn }) =>
        $ignitionOn ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'},
      transparent
    );
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
`;

export const CardHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

export const CardHeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  max-width: 42%;
`;

export const AssetImageBox = styled.div<{ $enableDarkTheme?: boolean }>`
  width: 72px;
  height: 40px;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
`;

export const PayloadBarContainer = styled.div<{ $enableDarkTheme?: boolean }>`
  width: 80%;
  height: 4px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 4px;
  overflow: hidden;
  position: absolute;
  bottom: 4px;
  left: 10%;
`;

export const PayloadBarFill = styled.div<{ $fillPercent: number }>`
  height: 100%;
  width: ${({ $fillPercent }) => Math.min(Math.max($fillPercent, 0), 100)}%;
  background: ${({ $fillPercent }) =>
    $fillPercent > 0
      ? 'linear-gradient(90deg, #fbbf24, #f59e0b)' // Amber colors
      : 'transparent'};
  transition: width 0.5s ease;
  border-radius: 4px;
`;

export const AssetIdentityBox = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
`;

export const BumperID = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#fff' : _light.textPrimary};
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StatusChip = styled.div<{
  $available: boolean;
  $enableDarkTheme?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.6)' : '#334155'};
  margin-top: 2px;
  flex-shrink: 0;
  white-space: nowrap;
  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

export const QueuingLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: #eab308;
  margin-top: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

export const LunchLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 12px;
  font-weight: 800;
  color: #f97316;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 2px;
  white-space: nowrap;

  &::after {
    content: '🍽️';
    font-size: 14px;
    filter: drop-shadow(0 0 4px rgba(249, 115, 22, 0.4));
  }
`;

export const CardDivider = styled.div<{ $enableDarkTheme?: boolean }>`
  height: 1px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.06)' : _light.borderGrey};
  margin: 2px 0;
`;

export const ShiftMetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 10px;
  margin-bottom: 4px;
`;

export const MetricsSectionTitle = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#ffffff' : 'rgba(0, 0, 0, 0.4)'};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 14px;
  margin-bottom: 12px;
  text-align: center;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
`;

export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 32px; /* Reduced from 38px */
  justify-content: flex-start;
`;

export const MetricLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 7px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255,255,255,0.65)' : 'rgba(100, 116, 139, 0.8)'};
  font-weight: 700;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MetricValue = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#ffffff' : '#1e293b'};
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

export const TirePressureContainer = styled.div<{ $enableDarkTheme?: boolean }>`
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LocationBadge = styled.div<{
  $color?: string;
  $enableDarkTheme?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 0;
  background: transparent;
  border: none;
  flex-shrink: 0;
`;

export const LocationLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255,255,255,0.85)' : 'rgba(100, 116, 139, 0.7)'};
  text-transform: uppercase;
`;

export const FuelProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`;

export const FuelProgressBarInner = styled.div<{ $enableDarkTheme?: boolean }>`
  flex: 1;
  height: 4px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 2px;
  overflow: hidden;
`;

export const FuelProgressFill = styled.div<{
  $fillPercent: number;
  $enableDarkTheme?: boolean;
}>`
  height: 100%;
  width: ${({ $fillPercent }) => Math.min(Math.max($fillPercent, 0), 100)}%;
  background: ${({ $fillPercent }) => {
    if ($fillPercent > 40) return '#22c55e'; // Green
    if ($fillPercent > 20) return '#eab308'; // Amber
    return '#ef4444'; // Red
  }};
  box-shadow: ${({ $fillPercent }) => {
    if ($fillPercent > 40) return '0 0 8px rgba(34, 197, 94, 0.4)';
    if ($fillPercent > 20) return '0 0 8px rgba(234, 179, 8, 0.4)';
    return '0 0 8px rgba(239, 68, 68, 0.4)';
  }};
  transition: width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 2px;
`;

export const FuelIconLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 8px;
  font-weight: 800;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(100, 116, 139, 0.9)'};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-right: 4px;
`;

export const FuelProgressLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 9px;
  font-weight: 700;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.6)' : 'rgba(100, 116, 139, 0.8)'};
  min-width: 24px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

export const FuelAlertChip = styled.div<{
  $level: string | null;
  $enableDarkTheme?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: ${({ $level }) =>
    $level === 'CRITICAL'
      ? 'rgba(239, 68, 68, 0.15)'
      : $level === 'WARNING'
      ? 'rgba(234, 179, 8, 0.15)'
      : 'rgba(34, 197, 94, 0.15)'};
  color: ${({ $level }) =>
    $level === 'CRITICAL'
      ? '#ef4444'
      : $level === 'WARNING'
      ? '#eab308'
      : '#22c55e'};
`;

export const LocationValue = styled.div<{ $color?: string }>`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: ${({ $color }) => $color ?? '#22c55e'};
`;

export const WorkshopDuration = styled.span<{ $enableDarkTheme?: boolean }>`
  font-size: 9px;
  font-weight: 500;
  margin-left: 4px;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255,255,255,0.4)' : 'rgba(100,116,139,0.6)'};
  letter-spacing: 0.01em;
`;

export const TPMSSection = styled.div<{ $enableDarkTheme?: boolean }>`
  display: flex;
  flex-direction: column;
  height: auto;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 4px 0;
`;

export const TPMSPlaceholder = styled.div<{ $enableDarkTheme?: boolean }>`
  height: 75px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  font-size: 11px;
  font-weight: 500;
  width: 100%;
`;

export const TPMSStatusLabel = styled.div<{
  $enabled?: boolean;
  $enableDarkTheme?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ $enabled }) =>
    $enabled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $enabled, $enableDarkTheme }) =>
    $enabled
      ? '#22c55e'
      : $enableDarkTheme
      ? 'rgba(255,255,255,0.85)'
      : 'rgba(0, 0, 0, 0.4)'};
  border: 1px solid
    ${({ $enabled, $enableDarkTheme }) =>
      $enabled
        ? 'rgba(34, 197, 94, 0.2)'
        : $enableDarkTheme
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

export const TPMSValuesRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
`;

export const TPMSValue = styled.div<{ $enableDarkTheme?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#e2e8f0' : '#1e293b'};
  min-width: auto;
  flex-shrink: 0;
  text-align: center;
  line-height: 1;

  div:first-child {
    font-size: 8px;
    line-height: 1;
    color: ${({ $enableDarkTheme }) =>
      $enableDarkTheme
        ? 'rgba(148, 163, 184, 0.8)'
        : 'rgba(100, 116, 139, 0.85)'};
    font-weight: 700;
  }
`;

export const TPMSSchematicContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 2px 0;
  position: relative;
`;

/* ───────── Production KPI ───────── */
export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;

  @media (max-width: 1600px) {
    gap: 12px;
  }
`;

export const KpiCard = styled.div<{ $enableDarkTheme?: boolean }>`
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme
      ? `linear-gradient(145deg, ${alpha(_dark.boxBackground, 0.92)}, ${alpha(
          _dark.pageBackground,
          0.95
        )})`
      : 'linear-gradient(145deg, #ffffff, #f1f5f9)'};
  backdrop-filter: blur(24px);
  box-shadow: ${({ $enableDarkTheme }) =>
    $enableDarkTheme
      ? '0 10px 40px rgba(0, 0, 0, 0.45)'
      : '0 10px 30px rgba(15, 23, 42, 0.08)'};
  border: 1px solid
    ${({ $enableDarkTheme }) =>
      $enableDarkTheme
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(15, 23, 42, 0.05)'};
  border-radius: 18px;
  padding: 24px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 0;

  @media (max-width: 1440px) {
    padding: 16px 14px;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ $enableDarkTheme }) =>
      $enableDarkTheme
        ? '0 20px 50px rgba(0, 0, 0, 0.6)'
        : '0 20px 40px rgba(15, 23, 42, 0.12)'};
    border-color: ${({ $enableDarkTheme }) =>
      $enableDarkTheme
        ? alpha(_dark.primary, 0.3)
        : alpha(_light.primary, 0.2)};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      ${alpha(_dark.primary, 0.6)},
      transparent
    );
  }
`;

export const KpiLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 8.5px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255,255,255,0.75)' : 'rgba(100, 116, 139, 0.8)'};
  font-weight: 900;
  margin-bottom: 12px;
`;

export const KpiValue = styled.div<{
  $accent?: boolean;
  $enableDarkTheme?: boolean;
}>`
  font-size: 38px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${({ $accent, $enableDarkTheme }) =>
    $accent ? _dark.primary : $enableDarkTheme ? '#f8fafc' : '#0f172a'};
  font-variant-numeric: tabular-nums;
`;

export const KpiUnit = styled.span`
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.35);
  margin-left: 4px;
`;

export const KpiSub = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 10px;
  font-weight: 500;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme
      ? 'rgba(148, 163, 184, 0.45)'
      : 'rgba(100, 116, 139, 0.6)'};
  margin-top: 12px;
`;

/* ───────── Mini asset row ───────── */
export const AssetMiniRow = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 10px;
  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${alpha(_dark.primary, 0.28)};
    border-radius: 4px;
  }
`;

export const MiniCard = styled.div<{
  $available: boolean;
  $enableDarkTheme?: boolean;
}>`
  flex-shrink: 0;
  width: 130px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme
      ? alpha(_dark.pageBackground, 0.88)
      : _light.boxBackground};
  border: 1px solid
    ${({ $available }) =>
      $available ? 'rgba(22,163,74,0.14)' : 'rgba(248,113,113,0.14)'};
  border-top: 2px solid
    ${({ $available }) => ($available ? '#16a34a' : '#f87171')};
  border-radius: 10px;
  padding: 12px;
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-2px);
  }
`;

export const MiniCardBumper = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#fff' : _light.textPrimary};
  margin-top: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MiniCardTrips = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: ${_dark.primary};
  margin-top: 4px;
  line-height: 1;
`;

export const MiniCardLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 8px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.3)' : _light.textSecondary};
  margin-top: 2px;
`;

export const EmptyState = styled.div<{ $enableDarkTheme?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.25)' : _light.textSecondary};
  font-size: 14px;
  letter-spacing: 0.06em;
`;

export const AssetSyncLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 9px;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  font-weight: 500;
  margin-top: 0;
  white-space: nowrap;
`;

export const AssetStateLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.85)' : '#475569'};
  margin-top: 0;
  letter-spacing: 0.03em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ═══════════════════════════════════════════════════════════════════════
// Fuel Level Bar Components
// ═══════════════════════════════════════════════════════════════════════

export const FuelBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
`;

export const FuelBarLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255,255,255,0.65)' : 'rgba(100, 116, 139, 0.8)'};
  flex-shrink: 0;
  width: 32px;
`;

export const FuelBarTrack = styled.div<{ $enableDarkTheme?: boolean }>`
  flex: 1;
  height: 5px;
  background: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 4px;
  overflow: hidden;
`;

export const FuelBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(Math.max($pct, 0), 100)}%;
  background: ${({ $pct }) =>
    $pct > 25
      ? 'linear-gradient(90deg, #16a34a, #22c55e)'
      : 'linear-gradient(90deg, #dc2626, #ef4444)'};
  border-radius: 4px;
  transition: width 0.6s ease;
`;

// ═══════════════════════════════════════════════════════════════════════
// Greaser Row Components
// ═══════════════════════════════════════════════════════════════════════

export const GreaserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0;
`;

export const GreaserLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? 'rgba(255,255,255,0.65)' : 'rgba(100, 116, 139, 0.8)'};
  flex-shrink: 0;
`;

export const GreaserStatusText = styled.span<{ $ok: boolean }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ $ok }) => ($ok ? '#22c55e' : '#ef4444')};
`;

// ═══════════════════════════════════════════════════════════════════════
// Alerts Row Components
// ═══════════════════════════════════════════════════════════════════════

export const AlertsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  margin-top: 2px;
  margin-bottom: 2px;
  flex-wrap: nowrap;
`;

export const AlertsLabel = styled.div<{ $enableDarkTheme?: boolean }>`
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ $enableDarkTheme }) =>
    $enableDarkTheme ? '#ffffff' : 'rgba(0, 0, 0, 0.4)'};
  margin-right: 2px;
  flex-shrink: 0;
`;

export const AlertChip = styled.div<{
  $variant: 'dtc' | 'rule' | 'tpms';
  $hasAlerts: boolean;
  $enableDarkTheme?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 2px 5px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid
    ${({ $variant, $hasAlerts }) =>
      !$hasAlerts
        ? 'rgba(100, 116, 139, 0.2)'
        : $variant === 'dtc'
        ? 'rgba(239, 68, 68, 0.35)'
        : $variant === 'rule'
        ? 'rgba(251, 191, 36, 0.35)'
        : alpha(_dark.primary, 0.35)};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
    filter: brightness(1.2);
    box-shadow: 0 0 10px
      ${({ $variant, $hasAlerts }) =>
        !$hasAlerts
          ? 'transparent'
          : $variant === 'dtc'
          ? 'rgba(239, 68, 68, 0.4)'
          : $variant === 'rule'
          ? 'rgba(251, 191, 36, 0.4)'
          : alpha(_dark.primary, 0.4)};
  }

  color: ${({ $variant, $hasAlerts }) =>
    !$hasAlerts
      ? 'rgba(100, 116, 139, 0.5)'
      : $variant === 'dtc'
      ? '#f87171'
      : $variant === 'rule'
      ? '#fbbf24'
      : _dark.primary};
  background: ${({ $variant, $hasAlerts }) =>
    !$hasAlerts
      ? 'transparent'
      : $variant === 'dtc'
      ? 'rgba(239, 68, 68, 0.08)'
      : $variant === 'rule'
      ? 'rgba(251, 191, 36, 0.08)'
      : alpha(_dark.primary, 0.08)};
`;

export const ShiftAlertTicker = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  min-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ShiftAlertName = styled.div<{
  $visible: boolean;
  $isFallback?: boolean;
}>`
  font-size: 9px;
  text-align: center;
  font-weight: 600;
  text-wrap: nowrap;
  color: ${({ $isFallback }) => ($isFallback ? '#ffffff' : '#de6262')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0px' : '4px')});
  transition: all 0.4s ease;
  line-height: 1.3;
  letter-spacing: 0.01em;
  white-space: normal;
  word-break: break-word;
  margin-top: 8px;
  margin-bottom: 8px;
  pointer-events: none;
`;
